import React, { useEffect, useMemo, useRef, useState } from "react";
import * as style from "./UserGroupsPanelContent.module.scss";
import Typography from "../../../Components/Common/CprimeText";
import {
  Dropdown,
  TextInput,
  Pagination,
  DropdownType,
  Divider,
  DropdownOption,
} from "cprime-foundational-components";
import GridTable from "cvmgmt-cube-grid-table";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import CloseIcon from "@mui/icons-material/Close";
import {
  getGroupTypeApplicationsList,
  searchContactsList,
  setGroupData,
  getAssociateGroupsList,
} from "../../../store/slices/userGroupSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getRoleSetupDrodownData } from "../../../services/RequestAccess/RequestAccess";

interface UserGroupsPanelContentProps {
  isEdit: boolean;
  selectedUserGroup: any;
  adminUserGroups?: boolean;
  appId?: number;
  groupId?: number;
}

interface MembersList {
  sailPointMembers: string[];
  dlMembers: string[];
  defaultMembers: string[];
}

const getCount = (rowList: any, filters: any) => {
  let rows = [...(rowList || [])];
  if (filters?.length > 0) {
    filters?.forEach((filter: any) => {
      const { id, value } = filter;
      rows = rows?.filter((row: any) =>
        row[id]?.toLowerCase().includes(value.toLowerCase()),
      );
    });
  }
  return rows?.length || 0;
};

const columns = [
  { id: "name", name: "Name", size: 200, minSize: 200 },
  { id: "isPrimaryContact", name: "Primary Contact", size: 200, minSize: 200 },
  { id: "email", name: "Email", size: 200, minSize: 200 },
  { id: "region", name: "Region", size: 200, minSize: 200 },
  { id: "departmentCode", name: "Department Code", size: 200, minSize: 200 },
  { name: "Action", id: "action", type: "ACTION", size: 200, minSize: 200 },
];

const usageTypesDropdownOptions = [
  { id: "profileApprover", label: "Profile Approver" },
  { id: "appApprover", label: "App Approver" },
  { id: "admin", label: "Admin" },
  { id: "publishApprover", label: "Publish Approver" },
  { id: "other", label: "Other" },
];

const isValidAppId = (value: any) => {
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim();
  return normalized !== "" && normalized !== "0";
};

const UserGroupsPanelContent: React.FC<UserGroupsPanelContentProps> = ({
  isEdit,
  selectedUserGroup,
  adminUserGroups = false,
  appId,
  groupId,
}) => {
  const scssClass = style as any;
  const [gridData, setGridData] = useState<GridTableDataType>({
    columns,
    rows: [],
    totalCount: 0,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [gridRefresh, setGridRefresh] = useState<number>(0);
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [groupNameOptions, setGroupNameOptions] = useState([]);
  const [groupNameInput, setGroupNameInput] = useState("");
  const [attributeTypeDropdownOptions, setAttributeTypeDropdownOptions] =
    useState<any[]>([]);
  const [
    attributeTypeValuesDropdownOptions,
    setAttributeTypeValuesDropdownOptions,
  ] = useState<{ [key: number | string]: DropdownOption[] }>({});
  const [groupTypeOptions, setGroupTypeOptions] = useState([]);
  const [dlList, setDLList] = useState([]);
  const [primaryContactOptions, setPrimaryContactOptions] = useState<any[]>([]);
  const [selectedDLList, setSelectedDLList] = useState<any>(null);
  const [dlListInput, setDLListInput] = useState("");
  const [selectedPrimaryContact, setSelectedPrimaryContact] =
    useState<any>(null);
  const [selectedGroupName, setSelectedGroupName] = useState<any>(null);
  const [primaryContactInput, setPrimaryContactInput] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [groupMemberOptions, setGroupMemberOptions] = useState<any[]>([]);
  const [membersList, setMembersList] = useState<MembersList>({
    sailPointMembers: [],
    dlMembers: [],
    defaultMembers: [],
  });
  const [groupMemberInput, setGroupMemberInput] = useState("");
  const [enableSpecifyGroupType, setEnableSpecifyGroupType] = useState(false);
  const [newList, setNewList] = useState<any[]>([]);
  const [groupNameEdited, setGroupNameEdited] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [associateGroupOptions, setAssociateGroupOptions] = useState<any[]>([]);
  const [selectedAssociateGroups, setSelectedAssociateGroups] = useState<any[]>(
    [],
  );
  const [isAssociateGroupsLoading, setIsAssociateGroupsLoading] =
    useState(false);
  const [conditionCount, setConditionCount] = useState<number>(0);
  // Tracks the resolved numeric appId for use in associate groups API
  const [selectedAppId, setSelectedAppId] = useState<
    number | string | undefined
  >(appId);
  const lastFetchedAttributeAppIdRef = useRef<number | string | undefined>(
    undefined,
  );
  // Prevents the conditions-init effect from re-running when attributeTypeDropdownOptions
  // changes after the user manually changes the Usage Type mid-edit.
  const conditionsInitializedRef = useRef(false);

  const dispatch = useDispatch();
  const form = useSelector((state: RootState) => state?.userGroups?.form);

  useEffect(() => {
    setGroupNameEdited(false);
  }, []);

  // Initializes all form fields for edit mode (except Profile Approver conditions).
  // Must NOT depend on attributeTypeDropdownOptions to avoid resetting usageType
  // when the user changes it to "Profile Approver" (which triggers a fetch that
  // updates attributeTypeDropdownOptions and would re-run this effect).
  useEffect(() => {
    if (!(isEdit && selectedUserGroup)) return;
    conditionsInitializedRef.current = false;
    setIsLoadingMembers(true);
    const groupType = selectedUserGroup?.group_type;
    const usageType = usageTypesDropdownOptions.find(
      (item) => item.label === selectedUserGroup?.usage_type,
    );

    dispatch(
      setGroupData({
        groupName: selectedUserGroup?.group_name || "",
        groupType: groupType || "",
        application: selectedUserGroup?.application || "",
        primaryContact: selectedUserGroup?.primary_contact || "",
        distributionList: selectedUserGroup?.dl || "",
        groupMembers: selectedUserGroup?.group_members || [],
        usageType: usageType ? [usageType] : [],
        attributeType: {},
        attributeTypeValues: {},
      }),
    );
    if (selectedUserGroup?.usage_type !== "Profile Approver") {
      setConditionCount(0);
    }
    setMembersList({
      ...membersList,
      defaultMembers: selectedUserGroup?.group_members || [],
    });
    if (selectedUserGroup?.group_type !== "Other") {
      setEnableSpecifyGroupType(true);
      setSelectedGroupName({
        id: selectedUserGroup?.group_name,
        label: selectedUserGroup?.group_name,
      });
    }
    if (selectedUserGroup?.dl)
      setSelectedDLList({
        id: selectedUserGroup?.dl,
        label: selectedUserGroup?.dl,
      });

    if (selectedUserGroup?.primary_contact) {
      setSelectedPrimaryContact({
        id: selectedUserGroup?.primary_contact,
        label: selectedUserGroup?.primary_contact,
      });
    }

    (selectedUserGroup?.group_members || []).map((label: string) => {
      const parts = label?.split("|").map((p) => p?.trim());
      const primaryContactEmail = selectedUserGroup?.primary_contact;
      const isPrimary = parts[1] === primaryContactEmail;
      if (selectedUserGroup?.primary_contact && isPrimary && parts[0]) {
        setSelectedPrimaryContact({
          id: parts[0],
          label: parts[0],
        });
      }
    });
    setIsLoadingMembers(false);
  }, [isEdit, selectedUserGroup]);

  // Populates Profile Approver conditions on edit once attribute type options are loaded.
  useEffect(() => {
    if (!(isEdit && selectedUserGroup)) return;
    if (selectedUserGroup?.usage_type !== "Profile Approver") return;
    if (!attributeTypeDropdownOptions?.length) return;
    if (conditionsInitializedRef.current) return;

    conditionsInitializedRef.current = true;
    const fetchedConditions = {
      values: {} as any,
      attributeTypes: {} as any,
    };
    setConditionCount(selectedUserGroup?.conditions?.length || 0);
    selectedUserGroup?.conditions?.forEach((condition: any, index: number) => {
      fetchedConditions.attributeTypes[index] = [
        attributeTypeDropdownOptions.find(
          (item) => item.label === condition?.fieldName,
        ),
      ];
      fetchedConditions.values[index] = condition?.values?.map(
        (value: string, valueIndex: number) => ({
          id: Number(value),
          label: String(condition?.resolvedValues?.[valueIndex] || value),
        }),
      );
    });
    dispatch(
      setGroupData({
        attributeType: fetchedConditions.attributeTypes,
        attributeTypeValues: fetchedConditions.values,
      }),
    );
  }, [isEdit, selectedUserGroup, attributeTypeDropdownOptions]);

  useEffect(() => {
    if (isEdit) return;
    dispatch(
      setGroupData({
        groupName: "",
        groupType: "",
        application: "",
        primaryContact: "",
        distributionList: "",
        groupMembers: [],
        usageType: [],
        attributeType: {},
        attributeTypeValues: {},
      }),
    );
    setSelectedGroupName(null);
    setSelectedPrimaryContact(null);
    setNewList([]);
    setGridData({
      columns,
      rows: [],
      totalCount: 0,
    });
  }, [isEdit]);

  useEffect(() => {
    getGroupTypeApplications();
    dispatch(
      setGroupData({
        new_group_members: [],
      }),
    );
  }, []);

  const getGroupTypeApplications = async () => {
    try {
      const response = await getGroupTypeApplicationsList();
      const apps = response?.data?.application?.map(
        (app: { id: any; name: any }) => {
          return { id: app?.id, label: app?.name };
        },
      );
      const groupTypes = response?.data?.group_type?.map(
        (app: { id: any; name: any }) => {
          return { id: app?.id, label: app?.name };
        },
      );
      setApplicationOptions(apps);
      setGroupTypeOptions(groupTypes);
    } catch (err) {}
  };

  // Fetches associate group options; pre-populates on edit
  const fetchAssociateGroupsWithId = async (appIdToUse: number | string) => {
    if (!appIdToUse) return;
    try {
      setIsAssociateGroupsLoading(true);
      const response = await getAssociateGroupsList(
        appIdToUse,
        isEdit ? groupId : undefined,
      );
      const rawOptions = response?.data?.options || [];
      // Preserve groupId and roleId so we can match them during pre-population
      // and send the correct ids in the payload
      const options = rawOptions.map((opt: any) => ({
        id: opt?.id,
        label: opt?.label,
        type: opt?.type, // "Group" | "Role"
        groupId: opt?.groupId, // actual group id (null for Roles)
        roleId: opt?.roleId, // actual role id (null for Groups)
      }));
      setAssociateGroupOptions(options);

      // Pre-populate on edit:
      // - associated_groups_ids → match against opt.groupId (type === "Group")
      // - associated_roles_ids  → match against opt.roleId  (type === "Role")
      const groupIds: number[] = selectedUserGroup?.associated_groups_ids ?? [];
      const roleIds: number[] = selectedUserGroup?.associated_roles_ids ?? [];
      if (isEdit && (groupIds.length > 0 || roleIds.length > 0)) {
        const preSelected = options.filter(
          (opt: any) =>
            (opt.type === "Group" && groupIds.includes(opt.groupId)) ||
            (opt.type === "Role" && roleIds.includes(opt.roleId)),
        );
        setSelectedAssociateGroups(preSelected);
        dispatch(
          setGroupData({
            associateGroups: preSelected
              .filter((o: any) => o.type === "Group")
              .map((o: any) => o.groupId),
            associatedRoles: preSelected
              .filter((o: any) => o.type === "Role")
              .map((o: any) => o.roleId),
          }),
        );
      }
    } catch (err) {
      setAssociateGroupOptions([]);
    } finally {
      setIsAssociateGroupsLoading(false);
    }
  };

  const parseDropdownOptions = (options: any[]) => {
    return options
      ?.map((item: any) => {
        const nameKey = Object.keys(item).find((key) => key.endsWith("_name"));

        return {
          id: item.id,
          label: nameKey ? item[nameKey] : null,
        };
      })
      .filter(({ label }) => label);
  };

  // Fetches Regions, Plants and Distributors for dropdowns
  const fetchRegionsPlantsDistrubutorsDropdown = async (
    id: number | string,
  ) => {
    const response = await getRoleSetupDrodownData(id);
    if (response?.status === "SUCCESS" && response?.data) {
      const nextAttributeTypeOptions = Object.keys(response.data).map((key) => ({
        id: key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
      }));
      const nextAttributeTypeValues = Object.keys(response.data).reduce(
        (acc, key) => ({
          ...acc,
          [key]: parseDropdownOptions(response.data[key]),
        }),
        {} as { [key: number | string]: DropdownOption[] },
      );

      setAttributeTypeDropdownOptions(nextAttributeTypeOptions);
      setAttributeTypeValuesDropdownOptions(nextAttributeTypeValues);
    }
  };

  const getDropdownOptionsByAttributeType = (type: string) => {
    return attributeTypeValuesDropdownOptions?.[type] ?? [];
  };

  const getAvailableAttributeTypeOptions = (index: number) => {
    const selectedAttributeTypeIds = Object.entries(form?.attributeType || {})
      .filter(
        ([key, value]) =>
          Number(key) !== index &&
          Array.isArray(value) &&
          value.length > 0,
      )
      .map(([, value]: any[]) => value?.[0]?.id)
      .filter(Boolean);

    return attributeTypeDropdownOptions.filter((option: DropdownOption) => {
      const isCurrentSelection = !!form?.attributeType?.[index]?.some(
        (selectedOption: DropdownOption) => selectedOption?.id === option?.id,
      );
      return isCurrentSelection || !selectedAttributeTypeIds.includes(option.id);
    });
  };

  const resolveApplicationId = () => {
    if (isValidAppId(appId)) {
      return appId;
    }

    if (isValidAppId(selectedAppId)) {
      return selectedAppId;
    }

    if (!form?.application || !applicationOptions?.length) {
      return undefined;
    }

    const matchedApplication = (applicationOptions as any[]).find(
      (option: any) =>
        option?.label === form?.application ||
        String(option?.id) === String(form?.application),
    );

    return matchedApplication?.id;
  };

  const canAddMoreConditions =
    form?.usageType?.[0]?.id === "profileApprover" &&
    conditionCount < attributeTypeDropdownOptions.length &&
    getAvailableAttributeTypeOptions(conditionCount).length > 0;

  const resolvedUsageTypeSelectedOptions = (() => {
    const selected = form?.usageType?.[0];
    if (!selected) return [];
    const matched = usageTypesDropdownOptions.find(
      (item) => item.id === selected.id || item.label === selected.label,
    );
    return matched ? [matched] : [selected];
  })();

  // Admin mode: appId prop is available directly
  useEffect(() => {
    if (isValidAppId(appId)) {
      const resolvedAdminAppId = appId as number | string;
      setSelectedAppId(resolvedAdminAppId);
      fetchAssociateGroupsWithId(resolvedAdminAppId);
      fetchRegionsPlantsDistrubutorsDropdown(resolvedAdminAppId);
    }
  }, [appId, isEdit]);

  // Non-admin edit mode: applicationOptions may load after panel opens,
  // so trigger fetch whenever EITHER applicationOptions OR edit data arrives
  useEffect(() => {
    if (isValidAppId(appId)) return; // admin mode — handled above
    if (!isEdit || !selectedUserGroup?.application) return;
    if (!applicationOptions?.length) return;
    const appName = selectedUserGroup.application;
    const matched = (applicationOptions as any[]).find(
      (opt: any) => opt.label === appName || String(opt.id) === String(appName),
    );
    if (matched?.id) {
      setSelectedAppId(matched.id);
      fetchAssociateGroupsWithId(matched.id);
      fetchRegionsPlantsDistrubutorsDropdown(matched.id);
    }
  }, [applicationOptions, isEdit, selectedUserGroup?.application]);

  useEffect(() => {
    if (form?.usageType?.[0]?.id !== "profileApprover") {
      lastFetchedAttributeAppIdRef.current = undefined;
      return;
    }

    const resolvedAppId = resolveApplicationId();
    if (!isValidAppId(resolvedAppId)) {
      return;
    }

    if (selectedAppId !== resolvedAppId) {
      setSelectedAppId(resolvedAppId);
    }

    const shouldFetchAttributeTypes =
      lastFetchedAttributeAppIdRef.current !== resolvedAppId ||
      attributeTypeDropdownOptions.length === 0;

    if (shouldFetchAttributeTypes) {
      lastFetchedAttributeAppIdRef.current = resolvedAppId;
      fetchRegionsPlantsDistrubutorsDropdown(resolvedAppId);
    }
  }, [
    appId,
    selectedAppId,
    form?.application,
    form?.usageType,
    applicationOptions,
    attributeTypeDropdownOptions.length,
  ]);

  const getDLListOptions = async (query: string) => {
    try {
      if (query?.length >= 1) {
        const response = await searchContactsList(
          `api/groups/group-types-users?searchTerm=${query}`,
        );
        const dlList = response?.data?.dl_list?.map(
          (app: { mail: any; name: any; members: any }) => {
            return {
              id: app?.mail,
              label: app?.name,
              members: app?.members?.filter(
                (member: any) => member?.split("|")[1]?.trim() !== "N/A",
              ),
            };
          },
        );
        setDLList(dlList);
      } else {
        setDLList([]);
      }
    } catch (err) {
      // console.error("error", err);
    }
  };

  const searchGroupName = async (query: string, add = false) => {
    try {
      if (query?.length >= 1) {
        if (add) setIsLoadingMembers(true);
        const response = await searchContactsList(
          `api/groups/group-types-users?group_type=SailPoint&group_name=${query}`,
        );
        const gnList = response?.data?.group_name?.map(
          (groupName: { id: any; detail: any }) => {
            return {
              id: groupName?.id,
              label: groupName?.detail,
            };
          },
        );
        setGroupNameOptions(gnList);

        if (add && response?.data?.group_members_details?.length > 0) {
          setMembersList({
            ...membersList,
            sailPointMembers: response?.data?.group_members_details?.map(
              (row: any) => row?.detail,
            ),
            ...(!groupNameEdited && isEdit && { defaultMembers: [] }),
          });
          if (!groupNameEdited && isEdit) {
            setNewList([]);
            dispatch(setGroupData({ primaryContact: "" }));
            setGroupNameEdited(true);
            setSelectedPrimaryContact(null);
          } else {
            setNewList((prev) =>
              prev.filter(
                (item) => !membersList?.sailPointMembers.includes(item),
              ),
            );
          }
        } else if (add && response?.data?.group_members_details?.length === 0) {
          setMembersList({
            ...membersList,
            sailPointMembers: [],
            ...(!groupNameEdited && isEdit && { defaultMembers: [] }),
          });
          if (!groupNameEdited && isEdit) {
            setNewList([]);
            dispatch(setGroupData({ primaryContact: "" }));
            setGroupNameEdited(true);
            setSelectedPrimaryContact(null);
          } else {
            setNewList((prev) =>
              prev.filter(
                (item) => !membersList?.sailPointMembers.includes(item),
              ),
            );
          }
        }
      } else {
        setGroupNameOptions([]);
      }
    } catch (err) {
      // console.error("error", err);
    } finally {
      if (add) setIsLoadingMembers(false);
    }
  };

  const searchContact = async (query: string, type?: string) => {
    try {
      if (query?.length >= 1) {
        const response = await searchContactsList(
          `api/groups/group-types-users?searchTerm=${query}`,
        );
        const results =
          response?.data?.group_members_details?.map((contact: any) => ({
            id: contact?.id,
            label: contact?.detail,
          })) || [];
        const groupMembersResults =
          response?.data?.group_members_details?.map((contact: any) => ({
            id: contact?.id,
            label: contact?.detail,
          })) || [];
        if (type === "groupmember") setGroupMemberOptions(groupMembersResults);
        else setPrimaryContactOptions(results);
      } else {
        if (type === "groupmember") setGroupMemberOptions([]);
        else setPrimaryContactOptions([]);
      }
    } catch (err) {
      setPrimaryContactOptions([]);
    }
  };

  const handleSelectedMembersDelete = (rowData: any) => {
    const selectedId = rowData?.id;
    const selectedName = rowData?.name;
    if (!selectedId || !selectedName) return;
    setNewList((prev) => prev.filter((item) => !item.includes(rowData?.name)));
    setMembersList({
      ...membersList,
      defaultMembers: membersList?.defaultMembers?.filter(
        (item: any) => !item.includes(rowData?.name),
      ),
      sailPointMembers: membersList?.sailPointMembers?.filter(
        (item: any) => !item.includes(rowData?.name),
      ),
      dlMembers: membersList?.dlMembers?.filter(
        (item: any) => !item.includes(rowData?.name),
      ),
    });

    // Remove from new_group_members in Redux store if present
    dispatch(
      setGroupData({
        new_group_members: (form?.new_group_members || []).filter(
          (member: string) => {
            const namePart = member.split("|")[0].trim(); // extract the name
            return namePart !== selectedName;
          },
        ),
      }),
    );

    if (
      selectedPrimaryContact &&
      selectedPrimaryContact.label === rowData?.name
    ) {
      setSelectedPrimaryContact(null);
      dispatch(setGroupData({ primaryContact: "" }));
    }
  };

  useEffect(() => {
    setPagination({ ...pagination, page: 0 });
  }, [filters]);

  useEffect(() => {
    let rows = [...newList];
    if (enableSpecifyGroupType) {
      rows = [
        ...rows,
        ...(membersList?.sailPointMembers || []),
        ...(membersList?.defaultMembers || []),
      ];
    } else {
      rows = [
        ...rows,
        ...(membersList?.dlMembers || []),
        ...(membersList?.defaultMembers || []),
      ];
    }
    rows = Array.from(new Set(rows));
    setNewList(rows);
  }, [membersList, enableSpecifyGroupType]);

  const groupMemberRows = useMemo(() => {
    const newRows = newList?.map((row: any) => {
      const parts = row?.split("|").map((p: string) => p.trim());
      return {
        name: `${parts[0]}`,
        isPrimaryContact:
          selectedPrimaryContact?.label === parts[0] ? "Yes" : "No",
        email: parts[1],
        departmentCode: parts[2]?.split("Department:")[1]?.trim() || "",
        region: parts[3]?.split("Region:")[1]?.trim() || "",
        action: "Delete",
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`,
      };
    });
    dispatch(setGroupData({ groupMembers: newList }));
    setGridRefresh((prev) => prev + 1);
    return newRows;
  }, [newList]);

  useEffect(() => {
    const applyFiltersAndPagination = () => {
      try {
        let rows = [...(groupMemberRows || [])];
        filters.forEach((filter) => {
          const { id, value } = filter;
          rows = rows.filter((row: any) =>
            row[id]?.toLowerCase().includes((value as any).toLowerCase()),
          );
        });

        // Apply sorting
        sorting.forEach(({ id, desc }) => {
          rows.sort((a: any, b: any) => {
            const valA = a[id] || "";
            const valB = b[id] || "";
            if (valA < valB) return desc ? 1 : -1;
            if (valA > valB) return desc ? -1 : 1;
            return 0;
          });
        });

        // Apply pagination
        const start = pagination.page * pagination.rowsPerPage;
        const end = start + pagination.rowsPerPage;
        const paginatedRows = rows.slice(start, end);
        setGridData((prev) => ({
          ...prev,
          rows: paginatedRows,
          totalCount: rows?.length,
        }));
      } catch (err) {
        // console.error(err);
      }
    };

    applyFiltersAndPagination();
  }, [filters, sorting, pagination, groupMemberRows, gridRefresh]);

  return (
    <div className={scssClass.panelContent}>
      <Divider
        variant={"fullWidth"}
        sx={{
          marginBottom: "15px",
          width: "100%",
          marginLeft: "0px !important",
          backgroundColor: "#f8f8f9 !important",
        }}
      />
      <Typography className={scssClass.textDescription}>
        Group Details
      </Typography>
      <div className={scssClass.fieldsContent}>
        {!adminUserGroups && (
          <Dropdown
            selectedOptions={
              form?.application
                ? [{ label: form?.application, id: form?.application }]
                : []
            }
            aria-label="user_group_application"
            disabled={isEdit}
            className={scssClass.dropdown}
            width="100%"
            options={applicationOptions}
            placeholder="Select an option"
            label="Application"
            optionsTextLimit={200}
            onChange={(option) => {
              const selected = option[0]?.label || "";
              dispatch(setGroupData({ application: selected }));
              const newAppId = option[0]?.id;
              setSelectedAppId(newAppId);
              setAttributeTypeDropdownOptions([]);
              setAttributeTypeValuesDropdownOptions({});
              setAssociateGroupOptions([]);
              setSelectedAssociateGroups([]);
              dispatch(
                setGroupData({
                  associateGroups: [],
                  associatedRoles: [],
                  attributeType: {},
                  attributeTypeValues: {},
                }),
              );
              if (newAppId) {
                if (form?.usageType?.[0]?.id === "profileApprover") {
                  setConditionCount((prev) => (prev > 0 ? prev : 1));
                }
                fetchAssociateGroupsWithId(newAppId);
                fetchRegionsPlantsDistrubutorsDropdown(newAppId);
              }
            }}
          />
        )}

        <Dropdown
          selectedOptions={
            form?.groupType
              ? [{ label: form?.groupType, id: form?.groupType }]
              : []
          }
          aria-label="user_group_group_type"
          className={scssClass.dropdown}
          width="100%"
          options={groupTypeOptions}
          placeholder="Select an option"
          label="Group Type"
          optionsTextLimit={200}
          onChange={(option) => {
            const selected = option[0]?.label || "";
            dispatch(setGroupData({ groupType: selected }));
            setGroupNameInput("");
            setSelectedGroupName(null);
            dispatch(setGroupData({ groupName: "" }));
            if (selected !== "Other") {
              setNewList((prev) =>
                prev.filter((item) => !membersList?.dlMembers.includes(item)),
              );
              setEnableSpecifyGroupType(true);
              // Clear associate groups data when switching away from "Other"
              setSelectedAssociateGroups([]);
              dispatch(
                setGroupData({ associateGroups: [], associatedRoles: [] }),
              );
            } else {
              setNewList((prev) =>
                prev?.filter(
                  (item) => !membersList?.sailPointMembers.includes(item),
                ),
              );
              setEnableSpecifyGroupType(false);
            }
          }}
        />

        {enableSpecifyGroupType ? (
          <Dropdown
            selectedOptions={selectedGroupName ? [selectedGroupName] : []}
            aria-label="user_group_name"
            className={scssClass.dropdown}
            width="100%"
            options={groupNameOptions}
            placeholder="Search and select group name"
            label="Group Name"
            optionsTextLimit={200}
            noOptionsText={
              groupNameInput.trim() === ""
                ? "Please enter group name"
                : groupNameOptions?.length === 0
                  ? "No options"
                  : ""
            }
            type={DropdownType.FilterSingleSelect}
            inputValue={groupNameInput}
            onInputChange={(_event, value) => {
              const searchValue = value?.split("|")[0];
              setGroupNameInput(searchValue);
              if (debounceTimer) clearTimeout(debounceTimer);
              const timer = setTimeout(() => {
                searchGroupName(searchValue);
              }, 300);
              setDebounceTimer(timer);
            }}
            onChange={(options) => {
              const selected = options[0]?.label || "";
              if (!selected) return;
              setSelectedGroupName({
                id: options[0].id,
                label: options[0].label,
              });
              searchGroupName(options[0].label, true);
              setGroupNameInput((selected || "")?.trim());
              dispatch(setGroupData({ groupName: selected || "" }));
            }}
            onOpen={() => {
              if (!groupNameInput) searchGroupName("");
            }}
          />
        ) : (
          <TextInput
            value={form?.groupName}
            className={scssClass.textField}
            sx={{
              width: "100%",
              // "& .MuiInputBase-input": { padding: "0.75rem 1rem" },
              // "& .MuiInputBase-root": { height: "39.98px" },
            }}
            onChange={(e: any) => {
              dispatch(setGroupData({ groupName: e?.target?.value }));
            }}
            placeholder="Input text"
            label="Group Name"
          />
        )}

        {/* User Types dropdown */}
        <Dropdown
          selectedOptions={resolvedUsageTypeSelectedOptions}
          aria-label="usage_types_dropdown"
          className={scssClass.dropdown}
          width="100%"
          options={usageTypesDropdownOptions}
          placeholder="Select an usage type"
          label="Usage Type"
          optionsTextLimit={200}
          noOptionsText="No options"
          type={DropdownType.FilterSingleSelect}
          onChange={(option) => {
            dispatch(
              setGroupData({
                usageType: option ?? [],
                ...(option.length > 0 && option[0].id === "profileApprover"
                  ? {}
                  : { attributeType: {}, attributeTypeValues: {} }),
              }),
            );
            if (option.length > 0 && option[0].id === "profileApprover") {
              setConditionCount(1);
              const resolvedAppId = resolveApplicationId();
              if (isValidAppId(resolvedAppId)) {
                if (selectedAppId !== resolvedAppId) {
                  setSelectedAppId(resolvedAppId);
                }
                fetchRegionsPlantsDistrubutorsDropdown(resolvedAppId);
              }
            } else {
              setConditionCount(0);
            }
          }}
        />

        {/* Conditions Section */}
        {form?.usageType?.length > 0 &&
          form?.usageType[0].id === "profileApprover" && (
            <div className={scssClass.profileApproverUserTypeSection}>
              {Array.from({ length: conditionCount }, (_, index) => (
                <div
                  className={scssClass.profileApproverUserTypeSectionRow}
                  key={`condition-group_-${index}`}
                >
                  <Dropdown
                    selectedOptions={form.attributeType?.[index] ?? []}
                    aria-label={`attribute_type_dropdown_${index}`}
                    className={scssClass.dropdown}
                    options={getAvailableAttributeTypeOptions(index)}
                    placeholder="Select attribute type"
                    label="Attribute Type"
                    optionsTextLimit={200}
                    loading={isAssociateGroupsLoading}
                    loadingText="Loading..."
                    noOptionsText="No options"
                    type={DropdownType.FilterSingleSelect}
                    onChange={(option) => {
                      dispatch(
                        setGroupData({
                          attributeType: {
                            ...form.attributeType,
                            [index]: option.length ? option : [],
                          },
                          attributeTypeValues: {
                            ...form?.attributeTypeValues,
                            [index]: [],
                          },
                        }),
                      );
                    }}
                    dropdownSx={{
                      maxWidth: "20rem",
                    }}
                  />
                  <Dropdown
                    selectedOptions={
                      form.attributeTypeValues?.[index]
                        ? form.attributeTypeValues?.[index]
                        : []
                    }
                    aria-label={`regions_dropdown_${index}`}
                    className={scssClass.dropdown}
                    options={getDropdownOptionsByAttributeType(
                      form.attributeType?.[index] &&
                        form.attributeType?.[index].length > 0
                        ? form.attributeType?.[index][0].id
                        : "",
                    )}
                    placeholder={`Select ${form.attributeType?.[index] && form.attributeType?.[index].length > 0 ? form.attributeType?.[index][0].id : "values"}`}
                    label={`${form.attributeType?.[index] && form.attributeType?.[index].length > 0 ? form.attributeType?.[index][0].label : "Values"}`}
                    optionsTextLimit={200}
                    loading={isAssociateGroupsLoading}
                    loadingText="Loading..."
                    noOptionsText="No options"
                    disabled={
                      !form.attributeType?.[index] ||
                      form.attributeType?.[index].length === 0
                    }
                    type={DropdownType.FilterMultiSelect}
                    onChange={(options) => {
                      dispatch(
                        setGroupData({
                          attributeTypeValues: {
                            ...form?.attributeTypeValues,
                            [index]: options.length ? options : [],
                          },
                        }),
                      );
                    }}
                    dropdownSx={{
                      maxWidth: "20rem",
                    }}
                  />
                  <CloseIcon
                    className={scssClass.closeIcon}
                    onClick={() => {
                      setConditionCount((prev) => {
                        if (prev === 1) {
                          dispatch(
                            setGroupData({
                              usageType: [],
                              attributeType: {},
                              attributeTypeValues: {},
                            }),
                          );
                        } else if (
                          index + 1 !==
                          Object.keys(form.attributeType).length
                        ) {
                          const newFormAttributeType = {
                            ...form.attributeType,
                          };
                          const newFormAttributeTypeValues = {
                            ...form.attributeTypeValues,
                          };
                          for (
                            let i = index + 1;
                            i < Object.keys(form.attributeType).length;
                            i++
                          ) {
                            newFormAttributeType[i - 1] =
                              newFormAttributeType[i];
                            newFormAttributeTypeValues[i - 1] =
                              newFormAttributeTypeValues?.[i];
                          }
                          dispatch(
                            setGroupData({
                              attributeType: newFormAttributeType,
                              attributeTypeValues: newFormAttributeTypeValues,
                            }),
                          );
                        } else {
                          const newFormAttributeType = {
                            ...form.attributeType,
                          };
                          delete newFormAttributeType[index];
                          const newFormAttributeTypeValues = {
                            ...form.attributeTypeValues,
                          };
                          delete newFormAttributeTypeValues[index];
                          dispatch(
                            setGroupData({
                              attributeType: newFormAttributeType,
                              attributeTypeValues: newFormAttributeTypeValues,
                            }),
                          );
                        }

                        return prev > 0 ? prev - 1 : 0;
                      });
                    }}
                  />
                </div>
              ))}
              <button
                type="button"
                disabled={!canAddMoreConditions}
                onClick={() =>
                  setConditionCount((prev) =>
                    Math.min(prev + 1, attributeTypeDropdownOptions.length),
                  )
                }
                className={scssClass.roleConditionsAddButton}
              >
                Add condition +
              </button>
            </div>
          )}

        {(() => {
          const showAssociateGroups = form?.groupType === "Other";
          return showAssociateGroups ? (
            <Dropdown
              selectedOptions={selectedAssociateGroups}
              aria-label="user_group_associate_groups"
              className={scssClass.dropdown}
              width="100%"
              options={associateGroupOptions}
              placeholder="Select an option"
              label="Associate Groups (Optional)"
              optionsTextLimit={200}
              loading={isAssociateGroupsLoading}
              loadingText="Loading..."
              noOptionsText="No options"
              type={DropdownType.FilterMultiSelect}
              onChange={(options) => {
                const selected = options ?? [];
                setSelectedAssociateGroups(selected);
                dispatch(
                  setGroupData({
                    associateGroups: selected
                      .filter((o: any) => o.type === "Group")
                      .map((o: any) => o.groupId),
                    associatedRoles: selected
                      .filter((o: any) => o.type === "Role")
                      .map((o: any) => o.roleId),
                  }),
                );
              }}
            />
          ) : null;
        })()}

        <Dropdown
          selectedOptions={selectedDLList ? [selectedDLList] : []}
          aria-label="user_group_dl_list"
          className={scssClass.dropdown}
          width="100%"
          options={dlList}
          placeholder="Select an option"
          label="Distribution List (Optional)"
          optionsTextLimit={200}
          type={DropdownType.FilterSingleSelect}
          inputValue={dlListInput}
          onInputChange={(_event, value) => {
            const searchValue = value?.split("|")[0];
            setDLListInput(searchValue);
            if (debounceTimer) {
              clearTimeout(debounceTimer);
            }
            const timer = setTimeout(() => {
              getDLListOptions(searchValue);
            }, 300);
            setDebounceTimer(timer);
          }}
          onChange={(option) => {
            const selected = option[0]?.label || "";
            dispatch(setGroupData({ distributionList: selected }));
            setSelectedDLList({
              id: option[0].id,
              label: option[0]?.label,
            });
            setNewList((prev) =>
              prev.filter((item) => !membersList?.dlMembers.includes(item)),
            );
            setMembersList({
              ...membersList,
              dlMembers: (option[0] as any)?.members,
            });
          }}
          onOpen={() => {
            if (!dlListInput) getDLListOptions("");
          }}
        />

        <Dropdown
          selectedOptions={
            selectedPrimaryContact ? [selectedPrimaryContact] : []
          }
          aria-label="user_group_primary_contact"
          className={scssClass.dropdown}
          width="100%"
          options={primaryContactOptions}
          placeholder="Search and select user"
          noOptionsText={
            primaryContactInput.trim() === ""
              ? "Please enter user name"
              : primaryContactOptions?.length === 0
                ? "No options"
                : ""
          }
          label="Primary Contact"
          optionsTextLimit={200}
          type={DropdownType.FilterSingleSelect}
          inputValue={primaryContactInput}
          onInputChange={(_event, value) => {
            const searchValue = value?.split("|")[0];
            setPrimaryContactInput(searchValue);
            if (debounceTimer) {
              clearTimeout(debounceTimer);
            }
            const timer = setTimeout(() => {
              searchContact(searchValue, "primarycontact");
            }, 300);
            setDebounceTimer(timer);
          }}
          onChange={(option) => {
            const selected = option[0] || null;
            if (!option[0]) return null;
            const selectedParts = selected.label
              ?.split("|")
              .map((p: string) => p.trim());
            setSelectedPrimaryContact({
              id: selected.id,
              label: selectedParts[0],
            });
            setPrimaryContactInput((selectedParts[0] || "")?.trim());
            dispatch(setGroupData({ primaryContact: selectedParts[1] || "" }));
            setMembersList({
              ...membersList,
              defaultMembers: [
                ...(membersList?.defaultMembers || []),
                selected.label,
              ],
            });
            if (
              form?.groupType === "SailPoint" &&
              !membersList.sailPointMembers.includes(selected.label)
            ) {
              dispatch(
                setGroupData({
                  new_group_members: [
                    ...(form?.new_group_members || []),
                    selected.label,
                  ],
                }),
              );
            }
          }}
          onOpen={() => {
            if (!primaryContactInput) searchContact("", "primarycontact");
          }}
        />

        <Dropdown
          selectedOptions={[]}
          aria-label="user_group_group_members"
          className={scssClass.dropdown}
          width="100%"
          options={groupMemberOptions}
          placeholder="Search and select user"
          noOptionsText={
            groupMemberInput.trim() === ""
              ? "Please enter user name"
              : groupMemberOptions?.filter(
                    (opt) => !(form?.groupMembers || []).includes(opt?.label),
                  )?.length === 0
                ? "No options"
                : ""
          }
          label="Group Members"
          optionsTextLimit={200}
          type={DropdownType.FilterSingleSelect}
          inputValue={groupMemberInput}
          onInputChange={(_event, value) => {
            const searchValue = value?.split("|")[0];
            setGroupMemberInput(searchValue);
            if (debounceTimer) clearTimeout(debounceTimer);
            const timer = setTimeout(() => {
              searchContact(searchValue, "groupmember");
            }, 300);
            setDebounceTimer(timer);
          }}
          onChange={(options) => {
            const selected = options[0];
            if (!selected) return;
            setMembersList({
              ...membersList,
              defaultMembers: [
                ...(membersList?.defaultMembers || []),
                selected.label,
              ],
            });
            if (
              form?.groupType === "SailPoint" &&
              !membersList.sailPointMembers.includes(selected.label)
            ) {
              dispatch(
                setGroupData({
                  new_group_members: [
                    ...(form?.new_group_members || []),
                    selected.label,
                  ],
                }),
              );
            }
            setGroupMemberInput("");
          }}
          onOpen={() => {
            if (!groupMemberInput) searchContact("", "groupmember");
          }}
        />
        <GridTable
          style={{ height: "calc(100vh - 35rem)", minHeight: "16.5rem" }}
          aria-label="selected_group_members"
          data={gridData}
          customPayload={true}
          isPagination={true}
          isCustomizeColumns={false}
          isSorting={true}
          isFiltering={true}
          title={"Selected Group Members"}
          pinning={{ left: [], right: ["action"] }}
          refresh={gridRefresh}
          filters={filters}
          sorting={sorting}
          setSorting={setSorting}
          setFilters={setFilters}
          cellActions={[
            {
              event: "Delete",
              callback: (event, data) => {
                const rowData = data?.row?.original;
                handleSelectedMembersDelete(rowData);
              },
              icon: true,
            },
          ]}
          showLoader={true}
          loading={isLoadingMembers}
        />
        <Pagination
          sx={{ width: "100%" }}
          {...{
            ...pagination,
            count: getCount(groupMemberRows, filters),
            rowsPerPageOptions: [25, 50, 100, 250],
            onPageChange: (e, number) => {
              setPagination({ ...pagination, page: number });
            },
            onRowsPerPageChange: (evt) => {
              setPagination({
                page: 0,
                rowsPerPage: Number(evt?.target?.value),
              });
            },
          }}
        />
      </div>
    </div>
  );
};
export default UserGroupsPanelContent;
